import React, { useState, useEffect } from 'react';
import { api } from '../hooks/useApi';
import { formatCurrency } from '../lib/utils';
import NewsdeskSentInfo from './NewsdeskSentInfo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BookingDetailModal = ({ isOpen, onClose, booking, onSave }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (booking) {
      setFormData({
        rate: booking.rate || 0,
        page_preference: booking.page_preference || '',
        special_instructions: booking.special_instructions || '',
        is_taxable: booking.is_taxable || false,
        tax_rate: booking.tax_rate || 0,
        needs_factura: booking.needs_factura || false,
        status: booking.status || 'confirmed',
      });
    }
  }, [booking]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}`, formData);
      onSave();
    } catch (err) {
      console.error('Failed to update booking:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="booking-detail-modal">
        <DialogHeader>
          <DialogTitle>Booking Details</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <NewsdeskSentInfo booking={booking} />
          {/* Read-only info */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md">
            <div>
              <p className="text-[10px] text-[#94A3B8] uppercase">Advertiser</p>
              <p className="text-sm font-medium text-[#F8FAFC]">{booking.advertiser_name}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] uppercase">Product</p>
              <p className="text-sm font-medium text-[#F8FAFC]">{booking.product_type}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] uppercase">Edition</p>
              <p className="text-sm text-[#F8FAFC]">{booking.edition_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] uppercase">Page</p>
              <p className="text-sm text-[#F8FAFC]">{booking.page_number || 'Not placed'}</p>
            </div>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Rate</Label>
              <Input type="number" step="0.01" value={formData.rate} onChange={(e) => setFormData(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {['draft', 'confirmed', 'production', 'invoiced', 'completed', 'cancelled'].map(s => (
                    <SelectItem key={s} value={s} className="text-[#F8FAFC]">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Page Preference</Label>
            <Input value={formData.page_preference} onChange={(e) => setFormData(prev => ({ ...prev, page_preference: e.target.value }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9" placeholder="e.g., Front section preferred" />
          </div>

          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Special Instructions</Label>
            <Textarea value={formData.special_instructions} onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] min-h-[60px]" />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={formData.is_taxable} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_taxable: c, tax_rate: c ? prev.tax_rate || 16 : 0, needs_factura: c ? prev.needs_factura : false }))} />
              <Label className="text-[#94A3B8] text-xs">Taxable</Label>
            </div>
            {formData.is_taxable && (
              <>
                <Input type="number" step="0.5" value={formData.tax_rate} onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-16 h-8 text-sm" />
                <span className="text-[#94A3B8] text-xs">%</span>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-booking-detail-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailModal;
